import { Delete, Upload } from "@mui/icons-material";
import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";

const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];
const MAX_WIDTH_PIXELS = 600;
const MAX_HEIGHT_PIXELS = 300;
const JPEG_QUALITY = 0.92;

interface Props {
	company: EpiCompanyConfigDTO;
	onChange: (changes: Partial<EpiCompanyConfigDTO>) => void;
}

const EpiCompanyLogo: FC<Props> = ({ company, onChange }) => {
	const [preview, setPreview] = useState<string>();
	const [busy, setBusy] = useState(false);
	const [version, setVersion] = useState(0);
	const { showAlert, showError } = useAlert();
	const service = new EpiInspectionService();

	useEffect(() => {
		let url: string | undefined;
		let active = true;
		const loadPreview = async () => {
			if (!company.hasLogo) {
				setPreview(undefined);
				return;
			}
			const response = await service.getLogo(company.companyId);
			if (active && response?.status === 200 && response.data?.size) {
				url = URL.createObjectURL(response.data);
				setPreview(url);
			}
		};
		loadPreview();
		return () => {
			active = false;
			if (url) {
				URL.revokeObjectURL(url);
			}
		};
	}, [company.companyId, company.hasLogo, version]);

	return (
		<div className="logo-empresa">
			<div className="logo-preview">
				{preview
					? <img src={preview} alt={`Logo de ${company.companyName}`} />
					: <Typography variant="caption">Logo padrão</Typography>
				}
			</div>
			<Button
				variant="outlined"
				component="label"
				startIcon={<Upload />}
				disabled={busy}
			>
				{company.hasLogo ? 'Trocar logo' : 'Enviar logo'}
				<input
					hidden
					type="file"
					accept={ACCEPTED_TYPES.join(',')}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						upload(e.target.files?.[0]);
						e.target.value = '';
					}}
				/>
			</Button>
			{company.hasLogo && (
				<Tooltip title="Remover logo">
					<span>
						<IconButton color="error" disabled={busy} onClick={() => remove()}>
							<Delete />
						</IconButton>
					</span>
				</Tooltip>
			)}
		</div>
	);

	async function upload(file?: File) {
		if (!file) {
			return;
		}
		if (!ACCEPTED_TYPES.includes(file.type)) {
			showError('Envie uma imagem PNG ou JPG.');
			return;
		}
		setBusy(true);
		try {
			const resized = await resizeImage(file);
			const form = new FormData();
			form.append('file', new File([resized], file.type === 'image/jpeg' ? 'logo.jpg' : 'logo.png',
				{ type: resized.type }));
			const response = await service.saveLogo(company.companyId, form);
			onChange({ hasLogo: response.data.hasLogo });
			setVersion(previous => previous + 1);
			showAlert('Logo salva com sucesso', "success");
		} catch (error: any) {
			showError(typeof error?.response?.data === 'string' ? error.response.data : 'Não foi possível salvar a logo.');
		}
		setBusy(false);
	}

	async function remove() {
		setBusy(true);
		try {
			const response = await service.deleteLogo(company.companyId);
			onChange({ hasLogo: response.data.hasLogo });
			showAlert('Logo removida. Os relatórios voltam a usar a logo padrão.', "success");
		} catch (error) {
			showError();
		}
		setBusy(false);
	}
};

function resizeImage(file: File) {
	return new Promise<Blob>((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const image = new Image();
		image.onload = () => {
			URL.revokeObjectURL(url);
			const scale = Math.min(1, MAX_WIDTH_PIXELS / image.naturalWidth, MAX_HEIGHT_PIXELS / image.naturalHeight);
			const canvas = document.createElement('canvas');
			canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
			canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
			canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
			canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Imagem inválida')),
				file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png', JPEG_QUALITY);
		};
		image.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('Imagem inválida'));
		};
		image.src = url;
	});
}

export default EpiCompanyLogo;
